import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" });
  } catch { return iso; }
};

const LEVEL_COLOR = {
  Crítico: { bg: "#dc2626", text: "#fff" },
  Alto:    { bg: "#f97316", text: "#fff" },
  Moderado:{ bg: "#f59e0b", text: "#fff" },
  Baixo:   { bg: "#10b981", text: "#fff" },
};

export default function AIAnalysisPDF() {
  const { cid, aid } = useParams();
  const [data, setData] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/psy/campaigns/${cid}/ai-analyses/${aid}`),
      api.get(`/psy/campaigns/${cid}/results`),
    ])
      .then(([a, r]) => { setData(a.data); setResults(r.data); })
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar"));
  }, [cid, aid]);

  useEffect(() => {
    if (data && results) {
      // Wait for fonts/images to settle then trigger print
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [data, results]);

  if (error) return <div className="p-10 text-center text-rose-700">{error}</div>;
  if (!data || !results) return <div className="p-10 text-center text-slate-400">Carregando relatório premium…</div>;

  const a = data.analysis;
  const c = results.campaign;
  const test = results.test;
  const company = c.company_snapshot || {};
  const scoring = results.scoring;

  return (
    <div className="pdf-root">
      <style>{`
        @page { size: A4; margin: 18mm 14mm 18mm 14mm; }
        @media print {
          body { background: #fff !important; }
          .pdf-root { font-family: 'Inter', -apple-system, sans-serif; color: #0f172a; }
          .page-break { page-break-before: always; }
          .no-print { display: none !important; }
        }
        .pdf-root { font-family: 'Inter', sans-serif; color: #0f172a; max-width: 760px; margin: 0 auto; padding: 30px; background: #fff; }
        .pdf-h1 { font-size: 28px; font-weight: 800; line-height: 1.15; letter-spacing: -0.02em; }
        .pdf-h2 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #4338ca; margin: 22px 0 8px; border-bottom: 2px solid #e0e7ff; padding-bottom: 6px; }
        .pdf-h3 { font-size: 13px; font-weight: 700; color: #0f172a; margin: 14px 0 5px; }
        .pdf-p { font-size: 11.5px; line-height: 1.65; color: #1e293b; margin: 8px 0; text-align: justify; }
        .pdf-meta { font-size: 10px; color: #64748b; }
        .pdf-table { width: 100%; font-size: 11px; border-collapse: collapse; margin: 8px 0; }
        .pdf-table th, .pdf-table td { padding: 7px 9px; text-align: left; border-bottom: 1px solid #e2e8f0; }
        .pdf-table th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.04em; color: #475569; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; }
        .cover { padding: 70px 0 50px; border-bottom: 4px solid #4338ca; margin-bottom: 28px; }
        .factor-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; margin: 6px 0; background: #f8fafc; }
        .rec-card { border-left: 3px solid #10b981; background: #ecfdf5; padding: 9px 12px; margin: 7px 0; border-radius: 0 6px 6px 0; }
        .disclaimer { background: #fffbeb; border: 1px solid #fde68a; padding: 10px 12px; border-radius: 6px; font-size: 10.5px; color: #78350f; margin-top: 20px; }
        .footer-bar { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9.5px; color: #94a3b8; display: flex; justify-content: space-between; }
        .toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
        .toolbar button { background: #4338ca; color: #fff; border: 0; padding: 8px 14px; font-weight: 600; border-radius: 6px; cursor: pointer; font-size: 12px; }
        .toolbar button.secondary { background: #fff; color: #4338ca; border: 1px solid #4338ca; }
      `}</style>

      <div className="toolbar no-print">
        <button onClick={() => window.print()}><i className="fa-solid fa-print"></i> Imprimir / Salvar PDF</button>
        <button className="secondary" onClick={() => window.close()}>Fechar</button>
      </div>

      {/* COVER */}
      <section className="cover">
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 30 }}>
          <img src={LOGO_URL} alt="Sollute" style={{ height: 42 }} />
          <div style={{ borderLeft: "2px solid #e0e7ff", paddingLeft: 14 }}>
            <div className="pdf-meta" style={{ textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: "#4338ca" }}>
              Sollute Ocupacional · NR-01
            </div>
            <div className="pdf-meta">Central de Testes Psicossociais</div>
          </div>
        </div>

        <div style={{ background: "#4338ca", color: "#fff", padding: "5px 12px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", display: "inline-block", textTransform: "uppercase" }}>
          Análise Interpretativa por IA
        </div>

        <h1 className="pdf-h1" style={{ marginTop: 20 }}>
          Diagnóstico psicossocial de {company.nome_fantasia || company.razao_social || "—"}
        </h1>
        <p className="pdf-p" style={{ fontSize: 13, color: "#475569", marginTop: 8 }}>
          {c.titulo}
        </p>

        <table style={{ width: "100%", fontSize: 11, marginTop: 28, color: "#1e293b" }}>
          <tbody>
            <tr><td style={{ padding: "5px 0", color: "#64748b", width: "32%" }}>Instrumento aplicado</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{test.name}</td></tr>
            {c.setor_ghe && <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Setor / GHE</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{c.setor_ghe}</td></tr>}
            {c.funcao && <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Função / Cargo</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{c.funcao}</td></tr>}
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Trabalhadores alvo</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{c.n_trabalhadores || "—"}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Respondentes efetivos</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{scoring.response_count} {results.participacao_pct != null && <span style={{ color: "#64748b", fontWeight: 400 }}>({results.participacao_pct}% de participação)</span>}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Score global</td>
              <td style={{ padding: "5px 0", fontWeight: 700 }}>
                {scoring.overall_score.toFixed(2)} <span className="badge" style={{ background: LEVEL_COLOR[scoring.overall.label]?.bg || "#94a3b8", color: "#fff", marginLeft: 8 }}>{scoring.overall.label}</span>
              </td>
            </tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Data da análise</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{fmtDate(data.created_at)}</td></tr>
            <tr><td style={{ padding: "5px 0", color: "#64748b" }}>Gerado por</td><td style={{ padding: "5px 0", fontWeight: 600 }}>{data.generated_by} · Claude Sonnet 4.6</td></tr>
          </tbody>
        </table>
      </section>

      {/* RESUMO EXECUTIVO */}
      <section>
        <h2 className="pdf-h2">1. Resumo Executivo</h2>
        <p className="pdf-p" style={{ fontSize: 12, fontStyle: "italic", color: "#1e293b", borderLeft: "3px solid #4338ca", paddingLeft: 12 }}>
          {a.resumo_executivo}
        </p>
      </section>

      {/* PERFIL */}
      <section>
        <h2 className="pdf-h2">2. Perfil Geral da Organização</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line" }}>{a.perfil_organizacao}</div>
      </section>

      {/* FATORES CRÍTICOS */}
      {a.fatores_criticos?.length > 0 && (
        <section>
          <h2 className="pdf-h2">3. Principais Fatores Críticos</h2>
          {a.fatores_criticos.map((f, i) => (
            <div key={i} className="factor-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <strong style={{ fontSize: 12 }}>{f.dimensao}</strong>
                <span className="badge" style={{ background: LEVEL_COLOR[f.nivel]?.bg || "#94a3b8", color: "#fff" }}>{f.nivel} · {f.media?.toFixed?.(2) ?? f.media}</span>
              </div>
              <div style={{ fontSize: 11, color: "#475569" }}>{f.leitura}</div>
            </div>
          ))}
        </section>
      )}

      {/* RANKING */}
      <section>
        <h2 className="pdf-h2">4. Ranking dos Riscos</h2>
        <p className="pdf-p">{a.ranking_riscos}</p>
      </section>

      {/* INTERPRETAÇÃO POR DIMENSÃO */}
      <section className="page-break">
        <h2 className="pdf-h2">5. Interpretação por Dimensão</h2>
        {a.interpretacao_dimensoes?.map((d, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            <h3 className="pdf-h3">
              {d.dimensao}{" "}
              <span className="badge" style={{ background: LEVEL_COLOR[d.nivel]?.bg || "#94a3b8", color: "#fff", marginLeft: 6 }}>
                {d.nivel} · {d.media?.toFixed?.(2) ?? d.media}
              </span>
            </h3>
            <p className="pdf-p">{d.interpretacao}</p>
          </div>
        ))}
      </section>

      {/* RELAÇÃO ENTRE FATORES */}
      <section>
        <h2 className="pdf-h2">6. Relação entre Fatores</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line" }}>{a.relacao_fatores}</div>
      </section>

      {/* CAUSAS & CONSEQUÊNCIAS */}
      <section>
        <h2 className="pdf-h2">7. Causas Organizacionais e Consequências Potenciais</h2>
        <h3 className="pdf-h3">Causas organizacionais possíveis</h3>
        <ul style={{ fontSize: 11.5, lineHeight: 1.6, marginLeft: 18, color: "#1e293b" }}>
          {a.causas_organizacionais?.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
        <h3 className="pdf-h3" style={{ marginTop: 10 }}>Consequências potenciais</h3>
        <ul style={{ fontSize: 11.5, lineHeight: 1.6, marginLeft: 18, color: "#1e293b" }}>
          {a.consequencias_potenciais?.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </section>

      {/* RECOMENDAÇÕES */}
      {a.recomendacoes_prioritarias?.length > 0 && (
        <section className="page-break">
          <h2 className="pdf-h2">8. Recomendações Prioritárias</h2>
          {a.recomendacoes_prioritarias.map((r, i) => (
            <div key={i} className="rec-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <strong style={{ fontSize: 12 }}>
                  {r.ordem || i + 1}. {r.titulo}
                </strong>
                {r.horizonte && <span style={{ fontSize: 9.5, fontWeight: 700, color: "#065f46", textTransform: "uppercase", letterSpacing: "0.04em" }}>{r.horizonte}</span>}
              </div>
              <div style={{ fontSize: 11, color: "#1e293b", marginTop: 3 }}>{r.descricao}</div>
            </div>
          ))}
        </section>
      )}

      {/* CONCLUSÃO */}
      <section>
        <h2 className="pdf-h2">9. Conclusão Técnica</h2>
        <div className="pdf-p" style={{ whiteSpace: "pre-line", background: "#f8fafc", padding: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}>
          {a.conclusao_tecnica}
        </div>
      </section>

      {/* DISCLAIMER */}
      <div className="disclaimer">
        <strong>Aviso técnico:</strong> {a.disclaimer}
      </div>

      <footer className="footer-bar">
        <div>Sollute Ocupacional · NR-01 · Documento confidencial</div>
        <div>Análise gerada por IA · Modelo {data.model}</div>
      </footer>
    </div>
  );
}
